import "./index.css";
import App from "./App";
import React from "react";
import ReactDOM from "react-dom/client";
import { reactPlugin } from "./utils/AppInsights";
import reportWebVitals from "./reportWebVitals";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import { UserProvider } from "./context/UserContext";
import { SettingsProvider } from "./components/form/SettingsProvider";
import { isBoundaryHandled } from "./utils/errorLogger";

// React 18 dev mode re-dispatches errors caught by error boundaries so dev
// tools / CRA's react-error-overlay can show them.  Three dispatch paths exist
// depending on the React patch version and browser:
//   1. window.reportError(error)                    React 18.3+, modern Chrome
//   2. window.dispatchEvent(cancelable ErrorEvent)  React 18.2
//   3. window.setTimeout(() => { throw })           fallback, fires window.onerror
// All three are intercepted here at module level (runs before CRA's overlay
// registers its own listeners) so boundary-handled errors never reach the overlay.
(function suppressBoundaryDevOverlay() {
  // Path 1: non-cancelable; must intercept before dispatch
  type WinWithReportError = Window & { reportError?: (error: unknown) => void };
  const _origReportError = (window as WinWithReportError).reportError;
  (window as WinWithReportError).reportError = function (error: unknown) {
    if (isBoundaryHandled(error)) return;
    _origReportError?.call(window, error);
  };

  // Path 2: cancelable ErrorEvent — capture phase runs before CRA's bubble handler
  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      if (isBoundaryHandled(event.error)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    { capture: true },
  );

  // Path 3: setTimeout throw — return true from window.onerror to suppress
  const _origOnError = window.onerror;
  window.onerror = function (msg, src, line, col, error) {
    if (isBoundaryHandled(error)) return true;
    return _origOnError?.call(window, msg, src, line, col, error) ?? false;
  };
})();

const AppWithTracking = withAITracking(reactPlugin, App),
  root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

root.render(
  <React.StrictMode>
    <UserProvider>
      <Provider store={store}>
        <SettingsProvider>
          <AppWithTracking />
        </SettingsProvider>
      </Provider>
    </UserProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// To log results (for example: reportWebVitals(console.log))
// Or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
